import React, { FC, useState, useEffect } from "react";
import useFetch from "hooks/useFetch"; // Asegúrate de importar tu hook useFetch correctamente
import config from "config.json";
import StayDatesRangeInputView from "containers/ListingDetailPage/listing-stay-detail/StayDatesRangeInputView";
import GuestsInputView from "containers/ListingDetailPage/listing-stay-detail/GuestsInputView";
import MpForm from "components/Mercadopago/MpForm";
import { useNavigate } from "react-router-dom";
import { set } from "lodash";
import SectionHotelData from "containers/ListingDetailPage/(components)/SectionHotelData";
import SectionGuestDistribution from "containers/ListingDetailPage/(components)/SectionGuestDistribution";
import Button from "shared/Button/Button";
import { is } from "date-fns/locale";

interface CheckOutPagePageMainProps {
  className?: string;
}

interface Habitacion {
  cantidad_ocupantes: number;
  codigo_habitacion: string;
  descripcion_categoria: string;
  temporada: Temporada[];
  subtotal_habitacion: number;
  ocupantes: Ocupante[];
}

interface Temporada {
  id_temporada: number;
  descripcion_temporada: string;
  dias_temporada: number;
  condiciones: Condicion[];
  subtotal_temporada: number;
  total_temporada: number;
}

interface Condicion {
  codigo_condicion: string;
  descripcion_condicion: string;
  cantidad_ocupantes: number;
  base_personas_ocupadas: BasePersona[];
  subtotal: number;
}

interface BasePersona {
  base_por_persona: number;
  cant_personas_condicion: number;
  monto: number;
  subtotal: number;
}

interface Ocupante {
  id: number;
  dni: string;
  apellidoNombre: string;
  condicion: string;
  fechaNacimiento: string;
}
interface Occupant {
  codigo_habitacion: string;
  id: number;
  dni: string;
  apellido_nombre: string;
  condicion: string;
  fecha_nac: string;
}
interface PreferenceData {
  preference_code: string;
  monto_senia: string;
}
interface HotelImage {
  hotel_code: string;
  photos: string[];
}

interface HotelImageDataResponse {
  default_image: string;
  hoteles: HotelImage[];
}

const CheckOutPagePageMain: FC<CheckOutPagePageMainProps> = ({
  className = "",
}) => {
  const formatDate = (dateString?: string): string => {
    // Helper function to get the default date in YYYY-MM-DD format
    const getDefaultDate = (offsetDays: number = 0) => {
      const date = new Date();
      date.setDate(date.getDate() + offsetDays);
      return date.toISOString().split('T')[0];
    };

    // Retrieve dates from sessionStorage
    let startDisp = sessionStorage.getItem("start_disp");
    let endDisp = sessionStorage.getItem("end_disp");

    // Set default dates if they do not exist
    if (!startDisp) {
      startDisp = getDefaultDate(); // Today's date
      sessionStorage.setItem("start_disp", startDisp);
    }

    if (!endDisp) {
      endDisp = getDefaultDate(3); // Today's date plus 3 days
      sessionStorage.setItem("end_disp", endDisp);
    }

    // Use the provided dateString if available, otherwise use startDisp or endDisp
    const dateToFormat = dateString || startDisp || endDisp;

    // Function to parse date in MM/DD/YYYY format
    const parseMMDDYYYY = (date: string) => {
      const [month, day, year] = date.split('/').map(part => part.padStart(2, '0'));
      return new Date(`${year}-${month}-${day}`);
    };

    // Function to parse date in YYYY-MM-DD format
    const parseYYYYMMDD = (date: string) => {
      return new Date(date);
    };

    // Determine which format the input is in and parse accordingly
    let parsedDate: Date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateToFormat)) {
      // YYYY-MM-DD format
      parsedDate = parseYYYYMMDD(dateToFormat);
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateToFormat)) {
      // MM/DD/YYYY format with single-digit month or day
      parsedDate = parseMMDDYYYY(dateToFormat);
    } else {
      console.error('Formato de fecha no válido:', dateToFormat);
      return ''; // Return an empty string or handle as needed
    }

    // Check if the date is valid
    if (isNaN(parsedDate.getTime())) {
      console.error('Fecha no válida:', dateToFormat);
      return ''; // Return an empty string or handle as needed
    }

    return parsedDate.toISOString().split('T')[0];
  };
  
  function isPagoCompleto(): boolean {
    const storedDate: string | null = sessionStorage.getItem('start_disp');

    if (!storedDate) {
      return false; // Default to false if no date is stored
    }

    const startDate = new Date(storedDate); // Convert string to Date
    const currentDate = new Date();

    if (isNaN(startDate.getTime())) {
      throw new Error("Invalid date format in sessionStorage."); // Handle invalid dates
    }

    const diffInTime = startDate.getTime() - currentDate.getTime();
    const diffInDays = diffInTime / (1000 * 60 * 60 * 24);

    return diffInDays <= 3;
  }

  const pagoCompleto = isPagoCompleto();
  const [occupants, setOccupants] = useState<Occupant[]>([]);
  const hotel_code = sessionStorage.getItem("selected_hotel");
  const access_token = sessionStorage.getItem("user_token");
  const guest = sessionStorage.getItem("guests_checkout");
  const dateFrom = formatDate(sessionStorage.getItem("start_disp") ?? '');
  const dateTo = formatDate(sessionStorage.getItem("end_disp") ?? '');
  const [totalGuests, setTotalGuests] = useState<string>("");
  const [hotelImage, setHotelImage] = useState<string>("");
  const [confirmacion, setConfirmacion] = useState<boolean>(true);
  const additionalInfo = sessionStorage.getItem('informacion_adicional_reserva')
    ? JSON.parse(sessionStorage.getItem('informacion_adicional_reserva') || "{}").informacion_adicional_habitaciones
    : { "informacion_adicional_habitaciones": "No hay información disponible" };
  const usuario = sessionStorage.getItem("user_id") || "";
  const [preReservaPayload, setPreReservaPayload] = useState<any>(
    {
      es_confirmacion: confirmacion,
      hotel: hotel_code,
      desde: dateFrom,
      hasta: dateTo,
      ocupantes: JSON.parse(guest?.toString() || "[]"),
      informacion_adicional_habitaciones: additionalInfo,
      id_user : usuario,
      pago_total: isPagoCompleto()

    }
  );
  const [showPaymentForm, setShowPaymentForm] = useState<boolean>(false); // Nuevo estado para mostrar el formulario
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // Nuevo estado para mensajes de error
  const [hotel, setHotel] = useState<any>(null); // Estado inicial para el hotel
  // Estado para los datos mockeados
  const [datosMockeados, setDatosMockeados] = useState<any>(null);

  const { data: hotelImagesData, error: hotelImagesError, fetchData: fetchHotelImages } = useFetch<HotelImageDataResponse>(
    "GET",
    `${config.backend_url}/setia/hoteles/ver_todas_fotos`,
    { "Content-Type": "application/json" }
  );


  const { data, isLoading, error, fetchData } = useFetch(
    "GET",
    `${config.backend_url}/setia/hotel/detalle?codigo=${hotel_code}`,
    { "Content-Type": "application/json" }
  );

  const { data: dataPreReserva, isLoading: isLoadingPreReserva, error: errorPreReserva, fetchData: fetchDataPreReserva } = useFetch(
    "POST",
    `${config.backend_url}/setia/reservas/prereserva`,
    preReservaPayload,
    { "Content-Type": "application/json" }
  );

  useEffect(() => {
    const initialHeight = window.innerHeight; // Guardar la altura inicial del viewport

    const handleResize = () => {
      const currentHeight = window.innerHeight;
      if (initialHeight - currentHeight > 150) {
        // El teclado probablemente está abierto, evita re-renderizaciones innecesarias
        console.log('El teclado se ha abierto.');
      } else {
        console.log('Resize real detectado.');
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    fetchHotelImages(); // Llama para obtener las imágenes
    fetchData(); // Llama para obtener los detalles del hotel
  }, []);

  useEffect(() => {
    if (hotelImagesData) {
      const hotelData = hotelImagesData.hoteles.find(
        (hotel) => hotel.hotel_code === hotel_code
      );

      if (hotelData) {
        setHotelImage(hotelData.photos[0]);
      } else {
        setHotelImage(hotelImagesData.default_image);
      }
    }
  }, [hotelImagesData]);

  useEffect(() => {
    const reserva = dataPreReserva as any;
    if (dataPreReserva && reserva.preference_code) {
      sessionStorage.setItem("id_preferencia", reserva.preference_code);
      sessionStorage.setItem("titulo_preferencia", reserva.titulo_prereserva.replace(/\//g, ' '));
      sessionStorage.setItem("preference_amount", reserva.monto_senia);
      setShowPaymentForm(true);
    } else if (errorPreReserva) {
      setErrorMessage("Error al obtener el ID de preferencia de pago.");
    }
  }, [dataPreReserva, errorPreReserva]);

  useEffect(() => {
    const occupantsData = sessionStorage.getItem("guests_checkout");
    if (occupantsData) {
      setOccupants(JSON.parse(occupantsData));
    }

    fetchData(); // Llama a fetchData para obtener datos del backend

    fetchDataPreReserva();
  }, []);

  useEffect(() => {
    renderMercadopagoForm();
  }, [showPaymentForm]);

  useEffect(() => {
    if (dataPreReserva) {
      setDatosMockeados(dataPreReserva);
      setShowPaymentForm(true);
    }
  }, [dataPreReserva]);

  useEffect(() => {
    if (data) {
      setHotel(data); // Actualiza el estado del hotel con los datos del backend
    }
  }, [data]);

  const router = useNavigate();

  const renderSidebar = () => {

    const handleCancelPayment = () => {
      sessionStorage.removeItem("id_preferencia");
      sessionStorage.removeItem("preference_amount");
      sessionStorage.removeItem("roomsData");
      sessionStorage.removeItem("start_disp");
      sessionStorage.removeItem("end_disp");
      sessionStorage.removeItem("total_disp");
      sessionStorage.removeItem("guests_checkout");
      sessionStorage.removeItem("occupants");
      router('/listing-stay-detail', { replace: true });
    };

    const hotelCode = sessionStorage.getItem("selected_hotel") || "";
    const subtotal = sessionStorage.getItem("total_subtotal") || "";
    const total_noches = sessionStorage.getItem("total_noches") || "";

    return (

      <div className="listingSectionSidebar__wrap shadow-xl">
        {/* PRICE */}
        <div className="flex justify-between">
          <span className="text-3xl font-semibold">
            Reserva:
            {/* <span className="ml-1 text-base font-normal text-neutral-500 dark:text-neutral-400">
            /night
          </span> */}
          </span>
          {/* <StartRating /> */}
        </div>

        <SectionHotelData hotelCode={hotelCode} />
        {/* FORM */}
        <form className="flex flex-col border border-neutral-200 dark:border-neutral-700 rounded-3xl ">
          <StayDatesRangeInputView className="flex-1 z-[11]" />
          <div className="w-full border-b border-neutral-200 dark:border-neutral-700"></div>
          <GuestsInputView className="flex-1" />
          <div className="w-full border-b border-neutral-200 dark:border-neutral-700"></div>
          {/* datos de la distribucion de los huespedes */}
          <SectionGuestDistribution />
          <h1 className="pl-4 pb-2 text-sm font-semibold">Precio : {total_noches} noches = {datosMockeados?.monto_senia.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</h1>

        </form>

        {/* SUBMIT */}
        <div>
          <button onClick={handleCancelPayment} className={`border w-full px-4 py-1.5 pb-3 bg-white hover:bg-neutral-200 text-red-500 text-md sm:px-6 sm:py-2.5 rounded-full focus:outline-none selected`}>Cancelar</button>
        </div>
      </div>
    );
  };


  const renderMercadopagoForm = () => {

    const handleRedirectToMerecadoPago = () => {
      const id_preferencia = sessionStorage.getItem("id_preferencia");
      if (!id_preferencia) {
        alert("No se pudo obtener el ID de pago. Por favor, inténtalo de nuevo más tarde. ");
      }
      const url = `${config.mp_form}${id_preferencia}`;
      window.location.href = url;
    }
    return (
      <div >
        {showPaymentForm ? (
          <>
          <p className="text-blue-500 mt-2">Serás redirigido a MercadoPago para realizar el pago</p>
          <button  className="border mt-4 w-full px-4 py-1.5 pb-3 bg-blue-600 hover:bg-blue-800 text-white text-md sm:px-6 sm:py-2.5 rounded-full focus:outline-none selected" onClick={handleRedirectToMerecadoPago} >
            Realizar Pago
          </button>
          </>
        ) : (
          <>
            {errorMessage && <div className="text-red-500 mt-2">{errorMessage}</div>}
          </>
        )}
      </div>
    );
  };

  const renderMain = () => {
    const hotelCode = sessionStorage.getItem("selected_hotel") || "";
    return (
      <>
        <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8">
          <h2 className="text-3xl lg:text-4xl font-semibold">
            Pago de Reserva
          </h2>
          <div className="border-b border-neutral-200 dark:border-neutral-700"></div>

          {/* <form className="flex flex-center border border-neutral-200 dark:border-neutral-700 rounded-3xl ">
          <StayDatesRangeInputView className="w-full" />
          <GuestsInputView className="w-full" />
        </form> */}
          <div className="space-y-8  lg:hidden">
            <h2 className="text-2xl font-semibold">Hotel:</h2>
            <SectionHotelData hotelCode={hotelCode} />
            {/* FORM */}
            <form className="flex flex-col border border-neutral-200 dark:border-neutral-700 rounded-3xl ">
              <StayDatesRangeInputView className="flex-1 z-[11]" />
              <div className="w-full border-b border-neutral-200 dark:border-neutral-700"></div>
              <GuestsInputView className="flex-1" />
              <div className="w-full border-b border-neutral-200 dark:border-neutral-700"></div>
              {/* datos de la distribucion de los huespedes */}
              <SectionGuestDistribution />
            </form>
          </div>

          <h2 className="pl-2 text-2xl font-semibold">Pagás {datosMockeados?.monto_senia.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}  { pagoCompleto ? "( 100% de la reserva)" : "( 50% de la reserva)"}</h2>

        </div>
        {renderMercadopagoForm()}
      </>
    );
  };

  if (!hotel) {
    return null; // Evitar renderizar si no hay datos de hotel
  }

  return (
    <div className={`nc-CheckOutPagePageMain ${className}`}>
      <main className="container mt-11 mb-24 lg:mb-32 flex flex-col-reverse lg:flex-row">
        <div className="w-full lg:w-3/5 xl:w-2/3 lg:pr-10">
          {renderMain()}
        </div>
        <div className="hidden lg:block flex-grow">
          {renderSidebar()}
        </div>
      </main>
    </div>
  );
};

export default CheckOutPagePageMain;